<template>
  <div :class="$gStyle('mt-3 mb-6')">
    <SearchMask
      v-show="isSearchMaskVisible"
      @search="searchSpareparts()"
      @search-by-query="searchByQuery()"
    ></SearchMask>
    <div v-show="!isSearchMaskVisible">
      <DeviceInformation :class="$gStyle('mt-2')" />
      <ExplosionDrawings
        :class="$gStyle('my-6')"
        @carousel-images-rendered="jumpToSparePart()"
      >
      </ExplosionDrawings>
      <InformationText></InformationText>
      <PositionTable ref="sparePartTable"></PositionTable>

      <!-- Extra Stuff -->
      <ResponseModal ref="responseModal"></ResponseModal>
      <CustomerFormModal
        ref="customerFormModal"
        @show-success-response="responseModal?.show()"
        @show-error-response="responseModal?.showError()"
      ></CustomerFormModal>
      <StickyBanner
        @show-customer-form-modal="customerFormModal?.show()"
      ></StickyBanner>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { AxiosError } from 'axios';
  import { storeToRefs } from 'pinia';
  import { defineEmits, inject, useTemplateRef } from 'vue';
  import { useRoute } from 'vue-router';
  import CustomerFormModal from '../components/CustomerFormModal.vue';
  import DeviceInformation from '../components/DeviceInformation.vue';
  import ExplosionDrawings from '../components/ExplosionDrawings.vue';
  import ResponseModal from '../components/Helper/ResponseModal.vue';
  import InformationText from '../components/InformationText.vue';
  import PositionTable from '../components/PositionTable.vue';
  import SearchMask from '../components/SearchMask.vue';
  import StickyBanner from '../components/StickyBanner.vue';
  import { getLocalization } from '../localization/localizationService';
  import { useDataStore } from '../stores/data';

  const createToast: any = inject('createToast');
  const route = useRoute();
  const store = useDataStore();
  const {
    isSearchMaskVisible,
    serialnumber,
    serviceNumber,
    fileNames,
    articleNumber,
    page,
    pageSize
  } = storeToRefs(store);
  const {
    loadSerialnumberInfo,
    loadExternalProductName,
    loadContactPhone,
    loadFileNames,
    loadHotspotsPositions,
    loadHotSpots,
    loadMultiComponents,
    loadWebsiteConfigPage
  } = store;

  const customerFormModal = useTemplateRef('customerFormModal');
  const responseModal = useTemplateRef('responseModal');
  const sparePartTable = useTemplateRef('sparePartTable');

  const emit = defineEmits(['carousel-images-rendered']);

  //Load ContactPhone async
  loadContactPhone(getLocalization().value.Country).catch((err: AxiosError) => {
    createToast(err.message);
  });

  //Load WebsiteConfigs async
  loadWebsiteConfigPage(
    getLocalization().value.Locale,
    'HauCustomerServiceUrl',
    window.location.origin
  ).catch((err: AxiosError) => {
    createToast(err.message);
  });

  loadWebsiteConfigPage(
    getLocalization().value.Locale,
    'HauSparePartsFormLegalTextUrl',
    window.location.origin
  ).catch((err: AxiosError) => {
    createToast(err.message);
  });

  const searchSpareparts = async () => {
    //Get Articlenumber & Device Description
    await loadSerialnumberInfo(serialnumber.value).catch((err: AxiosError) => {
      createToast(err.message);
    });

    if (articleNumber.value) {
      //Get External Product Name async
      loadExternalProductName(
        articleNumber.value,
        getLocalization().value.Locale
      ).catch((_: AxiosError) => {
        //try with en_GB
        loadExternalProductName(articleNumber.value, 'en_GB').catch(
          (err: AxiosError) => {
            console.log(
              'Failed to load external product name with backup locale en_GB'
            );

            if (import.meta.env.DEV) {
              createToast(err.message);
            }
          }
        );
      });
    }

    //Get FileNames for given Articlenumber
    if (!serviceNumber.value) {
      return;
    }
    await loadFileNames(serviceNumber.value).catch((err: AxiosError) => {
      createToast(err.message);
    });

    //Get Hotspots Positions async
    loadHotspotsPositions(
      serviceNumber.value,
      getLocalization().value.Locale,
      fileNames.value
    ).catch((err: AxiosError) => {
      createToast(err.message);
    });

    //Get HotSpots async
    loadHotSpots(
      serviceNumber.value,
      getLocalization().value.Locale,
      page.value,
      pageSize.value
    ).catch((err: AxiosError) => {
      createToast(err.message);
    });

    //Get Mutlicomponents async
    loadMultiComponents(
      serviceNumber.value,
      getLocalization().value.Locale
    ).catch((err: AxiosError) => {
      createToast(err.message);
    });
  };

  const searchByQuery = async () => {
    await searchSpareparts();
  };

  const jumpToSparePart = async () => {
    //check if anker exists to a position => scroll to position in table
    if (!!route.hash) {
      sparePartTable.value?.scrollToSparePart();
    }
  };
</script>

<style lang="scss" scoped></style>
